import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { setupAPI, getUserErrorMessage } from '../lib/api';
import { clearSetupTourState, dispatchSetupTourStatusChanged, readSetupTourState, SETUP_PAYMENTS_STATUS_EVENT } from '../lib/setupTour';
import { needsPaymentSetup } from '../lib/paymentSetup';

const IDLE = { phase: 'idle' };

export default function useSetupCompletion({ orgId, paymentsStep, returnedFromStripe }) {
  const navigate = useNavigate();
  const [completion, setCompletion] = useState(IDLE);
  const request = useRef(0);
  const started = useRef(false);
  const lastOptions = useRef({});

  useEffect(() => {
    setCompletion(IDLE);
    started.current = false;
    request.current += 1;
    return () => { request.current += 1; };
  }, [orgId]);

  const finish = useCallback(async (options = {}) => {
    if (started.current || !orgId) return;
    started.current = true;
    lastOptions.current = options;
    const attempt = ++request.current;
    setCompletion({ phase: 'saving', ...options });
    try {
      await setupAPI.updateStatus({ onboarding_dismissed: true });
      if (attempt !== request.current) return;
      clearSetupTourState();
      dispatchSetupTourStatusChanged();
      setCompletion({ phase: 'success', ...options });
      navigate('/dashboard', { replace: true });
    } catch (error) {
      if (attempt !== request.current) return;
      setCompletion({ phase: 'error', error: getUserErrorMessage(error, 'Die Einrichtung konnte noch nicht abgeschlossen werden. Bitte versuche es erneut.'), ...options });
    }
  }, [orgId, navigate]);

  useEffect(() => {
    if (!paymentsStep || !orgId) return undefined;
    const onStatus = event => {
      const { orgId: statusOrg, status } = event.detail || {};
      const tourOrg = readSetupTourState()?.orgId;
      if (statusOrg !== orgId || (tourOrg && tourOrg !== orgId)) return;
      const active = Boolean(status?.connected && !needsPaymentSetup(status));
      // Completing the app tutorial does not activate payments or change Stripe.
      if (!active && !returnedFromStripe) return;
      finish({ paymentsActive: active, stripeSubmitted: status?.details_submitted === true });
    };
    window.addEventListener(SETUP_PAYMENTS_STATUS_EVENT, onStatus);
    return () => window.removeEventListener(SETUP_PAYMENTS_STATUS_EVENT, onStatus);
  }, [paymentsStep, orgId, returnedFromStripe, finish]);

  const retry = () => { started.current = false; finish(lastOptions.current); };
  const dismiss = () => {
    started.current = false;
    setCompletion(IDLE);
  };
  return { completion, finish, retry, dismiss };
}
